import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '../HoverTooltip';

export type NavSection = {
  cat: string;
  items: {
    slug: string;
    label: string;
    needsSetup?: boolean;
    // How many of the section's required fields are still empty. Route
    // creation only (see attachRequiredCounts): elsewhere a section shows
    // the "!" instead. Undefined or 0 renders nothing — a section with no
    // required fields carries no indicator, and the absence is the signal.
    requiredCount?: number;
    // The user has tried to save and this section is why it failed, so the
    // same chip goes red rather than a second mark appearing beside it.
    // It can be set with a count of 0: validateActionConfigs also rejects a
    // FILLED optional field (a bad pattern, say), and that still needs a
    // mark — the chip just has no number to show.
    requiredIsError?: boolean;
  }[];
};

// One section rendered on the page in stacked mode. `slug` must match a nav
// item's slug — that's how the nav knows which entry to highlight.
export type StackedSection = {
  slug: string;
  label?: React.ReactNode;
  // Sits on the label's line, right-aligned — where a section's on/off switch
  // goes, so it reads as belonging to the heading rather than to the fields.
  // Under the label, left of headerRight — the section's own note.
  description?: React.ReactNode;
  headerRight?: React.ReactNode;
  content: React.ReactNode;
};

type SelectionPanelProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerActions?: React.ReactNode;
  banner?: React.ReactNode;
  nav: NavSection[];
  activeSlug?: string;
  onSelectSection: (slug: string) => void;
  children: React.ReactNode; // the active section's content
  footer?: React.ReactNode; // Save/Cancel
  emptyNavNote?: string;
  // Render every section on the page and let the nav follow the reader instead
  // of choosing what renders. For screens with nothing to save section by
  // section, where a hidden section is work the user can't see they owe.
  // When set, `children`/`activeSlug` are ignored.
  stackedSections?: StackedSection[];
};

// The fixed top bar (70px desktop, 80px mobile) covers the top of the window, so
// every offset in stacked mode is measured from below it.
export const STICKY_TOP = 'top-[96px] md:top-[86px]';
const SCROLL_MARGIN = 'scroll-mt-[96px] md:scroll-mt-[86px]';
// The top of the readable area: the bar's height at the widest breakpoint, so a
// section hidden behind it doesn't count as seen.
const VIEWPORT_TOP = 86;

// Which stacked section the reader is looking at: the one filling most of the
// viewport, measured as a share of what could be on screen rather than in raw
// pixels.
const useScrollSpy = (slugs: string[], enabled: boolean) => {
  const [activeSlug, setActiveSlug] = useState<string | undefined>(slugs[0]);
  const refs = useRef<Record<string, HTMLElement | null>>({});

  const setRef = useCallback(
    (slug: string) => (el: HTMLElement | null) => {
      refs.current[slug] = el;
    },
    [],
  );

  const recompute = useCallback(() => {
    if (!enabled) return;
    const viewportTop = VIEWPORT_TOP;
    const viewportBottom = window.innerHeight;
    let best = slugs[0];
    let bestScore = -1;
    slugs.forEach((slug) => {
      const el = refs.current[slug];
      if (!el) return;
      const { top, bottom, height } = el.getBoundingClientRect();
      const visible =
        Math.min(bottom, viewportBottom) - Math.max(top, viewportTop);
      // A short section fully in view wins over a tall one showing a sliver —
      // in raw pixels the last, often short, section is unreachable.
      const score =
        visible / Math.max(1, Math.min(height, viewportBottom - viewportTop));
      // Ties go to the earlier section, so a boundary sitting exactly mid-screen
      // doesn't flicker between the two as you scroll.
      if (score > bestScore) {
        bestScore = score;
        best = slug;
      }
    });
    setActiveSlug(best);
  }, [enabled, slugs]);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        recompute();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, recompute]);

  // Sections appear and change height after the first paint (schemas load, a
  // form grows), so settle the highlight on layout and on any later resize.
  useLayoutEffect(() => {
    recompute();
    if (!enabled || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => recompute());
    Object.values(refs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, recompute]);

  const scrollTo = useCallback((slug: string) => {
    const el = refs.current[slug];
    if (!el) return;
    setActiveSlug(slug);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return { activeSlug, setRef, scrollTo };
};

// Presentational only: it knows nothing about integrations. The nav switches the
// content instead of scrolling to it, so exactly one section renders at a time.
const SelectionPanel = ({
  title,
  subtitle,
  headerActions,
  banner,
  nav,
  activeSlug,
  onSelectSection,
  children,
  footer,
  emptyNavNote,
  stackedSections,
}: SelectionPanelProps) => {
  const { t } = useTranslation();
  const stacked = !!stackedSections;
  // Keyed on the slugs themselves, not on the `stackedSections` array: every
  // host builds that array inline, so it is a new reference on every render.
  // Memoising on the reference never hits, which makes `recompute` a new
  // callback each time and tears down and rebuilds the scroll listeners and the
  // ResizeObserver on every keystroke in a form.
  const slugKey = (stackedSections ?? []).map((s) => s.slug).join('\n');
  const stackedSlugs = React.useMemo(
    () => (slugKey ? slugKey.split('\n') : []),
    [slugKey],
  );
  const spy = useScrollSpy(stackedSlugs, stacked);
  // The section under the pointer wins — it's the clearest statement of which
  // one the user means. Scroll position is the fallback.
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null);
  const highlightedSlug = stacked
    ? (hoveredSlug ?? spy.activeSlug)
    : activeSlug;
  const navList = (
    <>
      {nav.map((section) => (
        <div key={section.cat} className="mb-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ui-text-primary-text">
            {section.cat}
          </div>
          {section.items.length === 0 && emptyNavNote && (
            <div className="border-l-2 border-unlisted-gray-lines py-1 pl-2 text-sm italic text-ui-text-primary-text">
              {emptyNavNote}
            </div>
          )}
          {section.items.map((item) => (
            <button
              key={item.slug}
              type="button"
              title={item.label}
              className={`flex w-full items-center gap-1.5 border-l-2 py-1 pl-2 text-left text-sm ${
                item.slug === highlightedSlug
                  ? 'border-green-1 font-semibold text-green-1'
                  : 'border-unlisted-gray-lines text-ui-text-primary-text hover:border-green-1 hover:text-green-1'
              }`}
              aria-current={item.slug === highlightedSlug ? 'true' : undefined}
              onClick={() => {
                onSelectSection(item.slug);
                if (stacked) spy.scrollTo(item.slug);
              }}
            >
              {/* Wraps rather than truncating: a long action name ("Pull iNaturalist
                            Observations") is read whole, over two lines if it must. */}
              <span className="min-w-0 break-words">{item.label}</span>
              {(!!item.requiredCount || item.requiredIsError) && (
                <span
                  data-testid={`SelectionPanel-${item.slug}-required-count`}
                  className={`flex-none rounded border px-1.5 py-0.5 text-xs ${
                    item.requiredIsError
                      ? 'border-ui-status-error-destructive bg-ui-status-bg-error-bg text-ui-status-error-destructive'
                      : 'border-unlisted-gray-lines bg-unlisted-light-gray text-ui-text-primary-text'
                  }`}
                >
                  {item.requiredCount
                    ? t('{{count}} required', { count: item.requiredCount })
                    : t('Check fields')}
                </span>
              )}
              {item.needsSetup && (
                <HoverTooltip
                  content={t('Needs setup — required fields are missing')}
                  position="top"
                  portal
                  focusable={false}
                >
                  <span className="flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full border border-ui-status-error-destructive bg-ui-status-bg-error-bg text-[9px] font-bold text-ui-status-error-destructive">
                    !
                  </span>
                </HoverTooltip>
              )}
            </button>
          ))}
        </div>
      ))}
    </>
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentLabel =
    nav
      .flatMap((section) => section.items)
      .find((item) => item.slug === highlightedSlug)?.label ??
    nav[0]?.items[0]?.label ??
    t('Select a section');
  return (
    <div className="rounded-lg border border-teal-t30 bg-white">
      {/* No header bar when there's nothing to put in it: the Connection page
            already names the record in its h1, so repeating it here was noise. */}
      {(title || subtitle || headerActions) && (
        <div className="flex flex-wrap items-center gap-3 rounded-t-lg border-b border-unlisted-gray-lines bg-unlisted-light-gray px-3 py-3 md:px-5">
          {/* flex-1 from a zero basis: the title shares the row with the
                    actions and shrinks (long IDs truncate inside it) rather than
                    pushing them onto a new line; below 12rem it wraps instead. */}
          {title && (
            <span className="min-w-[12rem] max-w-full flex-1 text-lg font-bold text-ui-text-primary-text">
              {title}
            </span>
          )}
          {subtitle && (
            <span className="text-sm text-ui-text-primary-text">
              {subtitle}
            </span>
          )}
          {headerActions && (
            <span className="ml-auto flex items-center gap-3">
              {headerActions}
            </span>
          )}
        </div>
      )}

      <div
        className={`flex flex-col gap-3 px-3 py-4 md:flex-row md:gap-6 md:px-5 md:py-5 ${stacked ? 'md:items-start' : ''}`}
      >
        {/* Below md the side column would leave the fields a sliver, so the
                sections collapse into a picker above them. It renders the same
                list as the desktop nav, badges included. */}
        <div className="md:hidden" data-testid="SelectionPanel-mobile-picker">
          <button
            type="button"
            aria-expanded={pickerOpen}
            className="flex w-full items-center justify-between gap-2 rounded border border-ui-field-outline bg-teal-t10 px-3 py-2 text-left text-sm font-semibold text-ui-text-primary-text"
            onClick={() => setPickerOpen((open) => !open)}
          >
            <span className="truncate">{currentLabel}</span>
            <span aria-hidden="true">{pickerOpen ? '▴' : '▾'}</span>
          </button>
          {pickerOpen && (
            <div
              className="mt-2 rounded border border-unlisted-gray-lines p-3"
              onClick={() => setPickerOpen(false)}
            >
              {navList}
            </div>
          )}
        </div>
        {/* From md up: the only way to reach the other sections, so it is always
                visible there. Hosts size themselves to fit this column. */}
        <nav
          className={`hidden flex-none pt-1 md:block md:w-48 ${stacked ? `sticky ${STICKY_TOP}` : ''}`}
        >
          {navList}
        </nav>

        <div className="min-w-0 flex-1">
          {banner}
          {stacked
            ? stackedSections!.map((section) => (
                <section
                  key={section.slug}
                  ref={spy.setRef(section.slug)}
                  data-testid={`SelectionPanel-${section.slug}-section`}
                  onMouseEnter={() => setHoveredSlug(section.slug)}
                  // Moving between sections fires the new one's enter before
                  // this leave, so only clear while still pointed at.
                  onMouseLeave={() =>
                    setHoveredSlug((current) =>
                      current === section.slug ? null : current,
                    )
                  }
                  // Double the gap between stacked sections (was mb-6) so each
                  // section reads as its own block, not a continuation of the last.
                  className={`${SCROLL_MARGIN} mb-12 last:mb-0`}
                >
                  {(section.label ||
                    section.headerRight ||
                    section.description) && (
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-col">
                        <h2 className="text-base font-bold text-ui-text-primary-text">
                          {section.label}
                        </h2>
                        {section.description && (
                          <p className="mt-1 text-sm italic text-ui-text-primary-text">
                            {section.description}
                          </p>
                        )}
                      </div>
                      {section.headerRight}
                    </div>
                  )}
                  {section.content}
                </section>
              ))
            : children}
        </div>
      </div>

      {footer && (
        <div className="flex items-center gap-3 border-t border-unlisted-gray-lines px-5 py-3">
          {footer}
        </div>
      )}
    </div>
  );
};

export default SelectionPanel;
